// ==========================================
// AboutPage.js - Complete SEO Optimized
// ==========================================
// File: petconnect-v2/frontend/src/components/features/About/AboutPage.js

import React, { useEffect } from 'react';
import { ChevronDown, Heart, MapPin, MessageSquare, Users, Camera, CheckCircle, Search } from 'lucide-react';

const AboutPage = () => {
  useEffect(() => {
    // Set page title for SEO
    document.title = 'About PetConnectAI - Find Lost & Adopted Pets | Pet Lovers Community';
    
    // You can add meta tags here if using React Helmet or similar
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'PetConnectAI is a pet lovers community platform to help lost pets find their way home, connect adoptable animals with families, and reunite lost pets. \n Where AI precision meets the heart of pet lovers');
    }
  }, []);

  return (
    <div className="max-w-5xl mx-auto pb-12">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl shadow-lg p-6 sm:p-8 md:p-12 mb-8">
        <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4">🐾 About PetConnectAI</h1>
        <p className="text-base sm:text-lg md:text-xl opacity-90">
          Connecting Pet Lovers Around the World
        </p>
      </div>

      {/* Mission Section */}
      <div className="bg-white rounded-xl shadow-lg p-6 sm:p-8 md:p-10 mb-6">
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-4">Our Mission</h2>
        <p className="text-gray-700 leading-relaxed mb-4 text-base sm:text-lg">
          At <strong>PetConnectAI</strong>, we believe that every pet deserves a safe, loving home. 
          Our mission is to build a compassionate community of pet lovers who work together to help 
          lost pets find their way home, connect adoptable animals with caring families, and celebrate 
          the joy that pets bring to our lives.
        </p>
        <p className="text-gray-700 leading-relaxed text-base sm:text-lg">
          We provide a dedicated platform where pet owners, animal rescuers, and pet enthusiasts can 
          share information, post about missing or found pets, and collaborate to reunite families 
          with their beloved companions.
        </p>
      </div>

      {/* Why Choose Us */}
      <div className="bg-white rounded-xl shadow-lg p-6 sm:p-8 md:p-10 mb-6">
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-8">Why Choose PetConnectAI?</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Feature 1: Advanced Search */}
          <div className="flex gap-4">
            <span className="text-4xl flex-shrink-0">🔍</span>
            <div>
              <h3 className="font-bold text-gray-800 mb-2 text-lg">Advanced Search</h3>
              <p className="text-gray-600">
                Find pets by location, breed, type, and description with our intelligent search filters.
              </p>
            </div>
          </div>
          
          {/* Feature 2: Location-Based */}
          <div className="flex gap-4">
            <span className="text-4xl flex-shrink-0">🗺️</span>
            <div>
              <h3 className="font-bold text-gray-800 mb-2 text-lg">Location-Based Discovery</h3>
              <p className="text-gray-600">
                Discover pets in your area using our interactive map and AI-powered search.
              </p>
            </div>
          </div>
          
          {/* Feature 3: Messaging */}
          <div className="flex gap-4">
            <span className="text-4xl flex-shrink-0">💬</span>
            <div>
              <h3 className="font-bold text-gray-800 mb-2 text-lg">Direct Messaging</h3>
              <p className="text-gray-600">
                Connect directly with other pet lovers and exchange information about pets.
              </p>
            </div>
          </div>
          
          {/* Feature 4: Community */}
          <div className="flex gap-4">
            <span className="text-4xl flex-shrink-0">👥</span>
            <div>
              <h3 className="font-bold text-gray-800 mb-2 text-lg">Community Support</h3>
              <p className="text-gray-600">
                Build friendships with fellow pet enthusiasts and share your pet experiences.
              </p>
            </div>
          </div>
          
          {/* Feature 5: Photo Sharing */}
          <div className="flex gap-4">
            <span className="text-4xl flex-shrink-0">📸</span>
            <div>
              <h3 className="font-bold text-gray-800 mb-2 text-lg">Photo Sharing</h3>
              <p className="text-gray-600">
                Upload multiple photos to help identify and reunite lost pets with families.
              </p>
            </div>
          </div>
          
          {/* Feature 6: Success Tracking */}
          <div className="flex gap-4">
            <span className="text-4xl flex-shrink-0">✓</span>
            <div>
              <h3 className="font-bold text-gray-800 mb-2 text-lg">Success Tracking</h3>
              <p className="text-gray-600">
                Mark pets as reunited and celebrate success stories with the community.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Our Community */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl shadow-lg p-6 sm:p-8 md:p-10 mb-6">
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-6">Our Community</h2>
        <p className="text-gray-700 mb-6 text-base sm:text-lg">
          PetConnectAI is built by and for pet lovers. Our community includes:
        </p>
        <ul className="space-y-3 text-gray-700">
          <li className="flex items-center gap-3 text-base sm:text-lg">
            <span className="text-2xl">❤️</span> Pet owners searching for their lost companions
          </li>
          <li className="flex items-center gap-3 text-base sm:text-lg">
            <span className="text-2xl">🐕</span> Good Samaritans who've found stray pets
          </li>
          <li className="flex items-center gap-3 text-base sm:text-lg">
            <span className="text-2xl">🏠</span> Rescue organizations and shelters
          </li>
          <li className="flex items-center gap-3 text-base sm:text-lg">
            <span className="text-2xl">👨‍👩‍👧‍👦</span> Families looking to adopt their perfect pet
          </li>
          <li className="flex items-center gap-3 text-base sm:text-lg">
            <span className="text-2xl">🌟</span> Pet enthusiasts sharing tips and success stories
          </li>
        </ul>
      </div>

      {/* How It Works */}
      <div className="bg-white rounded-xl shadow-lg p-6 sm:p-8 md:p-10 mb-6">
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-8">How PetConnectAI Works</h2>
        <div className="space-y-6">
          {[
            { 
              num: '1', 
              title: 'Create Your Account',
              desc: 'Sign up in minutes to start posting and connecting with the pet lover community.'
            },
            { 
              num: '2', 
              title: 'Post About Your Pet',
              desc: 'Create posts for missing, found, or adoptable pets with photos and details.'
            },
            { 
              num: '3', 
              title: 'Search & Discover',
              desc: 'Browse posts by location, pet type, and other criteria to find what you need.'
            },
            { 
              num: '4', 
              title: 'Connect & Message',
              desc: 'Message other users to share information and arrange meetups.'
            },
            { 
              num: '5', 
              title: 'Celebrate Success',
              desc: 'Share your success stories and inspire others in our community.'
            }
          ].map(step => (
            <div key={step.num} className="flex gap-4 sm:gap-6">
              <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold text-lg">
                {step.num}
              </div>
              <div className="pt-1">
                <h3 className="font-bold text-gray-800 text-lg mb-1">{step.title}</h3>
                <p className="text-gray-600 text-base">{step.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Core Values */}
      <div className="bg-white rounded-xl shadow-lg p-6 sm:p-8 md:p-10 mb-6">
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-8">Our Core Values</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          <div className="text-center p-4">
            <div className="text-5xl mb-4">💚</div>
            <h3 className="font-bold text-gray-800 mb-2 text-lg">Compassion</h3>
            <p className="text-gray-600 text-base">
              We care deeply about the welfare of all animals.
            </p>
          </div>
          
          <div className="text-center p-4">
            <div className="text-5xl mb-4">🤝</div>
            <h3 className="font-bold text-gray-800 mb-2 text-lg">Community</h3>
            <p className="text-gray-600 text-base">
              Together, we can make a greater impact.
            </p>
          </div>
          
          <div className="text-center p-4">
            <div className="text-5xl mb-4">🎯</div>
            <h3 className="font-bold text-gray-800 mb-2 text-lg">Purpose</h3>
            <p className="text-gray-600 text-base">
              Every connection matters when reuniting families.
            </p>
          </div>
        </div>
      </div>

      {/* Call to Action */}
      <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl shadow-lg p-6 sm:p-8 md:p-12 text-center mb-8">
        <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4">
          Join Our Pet-Loving Community
        </h2>
        <p className="text-base sm:text-lg opacity-90 mb-6">
          Help reunite lost pets with their families and find your perfect furry companion today.
        </p>
        <button className="px-6 sm:px-8 py-2 sm:py-3 bg-white text-purple-600 rounded-lg font-bold hover:bg-gray-100 transition text-base sm:text-lg">
          Get Started Now
        </button>
      </div>

      {/* FAQ Section */}
      <div className="bg-white rounded-xl shadow-lg p-6 sm:p-8 md:p-10">
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-6">Frequently Asked Questions</h2>
        <div className="space-y-4">
          {[
            { 
              q: 'Is PetConnectAI free to use?', 
              a: 'Yes! PetConnectAI is completely free to use. You can create posts, search for pets, and message other users at no cost. We believe in making pet reunification accessible to everyone.'
            },
            { 
              q: 'How do I post a missing pet?', 
              a: 'Sign up or log in, go to your Home Feed, click "Create Post", select "Missing Pet" as the category, add photos and details about your pet, set the location using our map, and click Post. Your post will be visible to the entire community.'
            },
            { 
              q: 'Can I search for adoptable pets?', 
              a: 'Yes! Visit our Adoption section from the menu to browse all adoptable pets, or use the Search feature with detailed filters to find pets that match your preferences.'
            },
            { 
              q: 'How can I contact other users?', 
              a: 'Once you find a pet or user you are interested in, you can send them a direct message through the PetConnectAI messaging system. Just click the message button on their profile or post.'
            }
          ].map((faq, idx) => (
            <details 
              key={idx} 
              className="border border-gray-200 rounded-lg p-4 sm:p-5 cursor-pointer hover:border-purple-300 transition"
            >
              <summary className="font-bold text-gray-800 flex items-center justify-between text-base sm:text-lg">
                {faq.q}
                <ChevronDown size={20} className="flex-shrink-0" />
              </summary>
              <p className="text-gray-600 mt-3 text-base leading-relaxed">
                {faq.a}
              </p>
            </details>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AboutPage;